<template>
  <div class="floating-slim-layout">
    <FloatingSidebar
      :chat-unread="chatUnreadCount"
      @help-clicked="handleHelpClick"
      @profile-clicked="handleProfileClick"
    />

    <div class="main-content">
      <div class="layout-topbar">
        <!-- Notification button hidden until feature is ready
        <button class="layout-topbar__chat-btn" @click="showNotifications = !showNotifications" title="Notifications">
          <svg width="20" height="20" viewBox="0 0 256 256" fill="currentColor"><path d="M221.8,175.94C216.25,166.38,208,139.33,208,104a80,80,0,1,0-160,0c0,35.34-8.26,62.38-13.81,71.94A16,16,0,0,0,48,200H88.81a40,40,0,0,0,78.38,0H208a16,16,0,0,0,13.8-24.06ZM128,216a24,24,0,0,1-22.62-16h45.24A24,24,0,0,1,128,216ZM48,184c7.7-13.24,16-43.92,16-80a64,64,0,1,1,128,0c0,36.05,8.28,66.73,16,80Z"/></svg>
          <span v-if="unreadNotifCount > 0" class="layout-topbar__chat-badge">{{ unreadNotifCount > 9 ? '9+' : unreadNotifCount }}</span>
        </button>
        -->

        <div v-if="loading" class="layout-topbar__company layout-topbar__company--loading">
          <div class="layout-topbar__avatar layout-topbar__skeleton layout-topbar__skeleton--circle"></div>
          <span class="layout-topbar__skeleton layout-topbar__skeleton--text"></span>
        </div>
        <div v-else class="layout-topbar__company" @click="$router.push('/app/settings')">
          <div class="layout-topbar__avatar layout-topbar__avatar--initials">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 256 256"><path d="M240,210H222V96a14,14,0,0,0-14-14H142V32a14,14,0,0,0-21.77-11.64l-80,53.33A14,14,0,0,0,34,85.34V210H16a6,6,0,0,0,0,12H240a6,6,0,0,0,0-12ZM208,94a2,2,0,0,1,2,2V210H142V94ZM46,85.34a2,2,0,0,1,.89-1.66l80-53.34A2,2,0,0,1,130,32V210H46ZM110,112v16a6,6,0,0,1-12,0V112a6,6,0,0,1,12,0Zm-32,0v16a6,6,0,0,1-12,0V112a6,6,0,0,1,12,0Zm0,56v16a6,6,0,0,1-12,0V168a6,6,0,0,1,12,0Zm32,0v16a6,6,0,0,1-12,0V168a6,6,0,0,1,12,0Z"></path></svg>
          </div>
          <div class="layout-topbar__avatar layout-topbar__avatar--profile">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="40" height="40" rx="20" fill="var(--Accent-Green, #BCFF4E)"/>
              <mask id="mask-profile" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="40" height="40">
                <rect x="0.333" y="0.333" width="39.333" height="39.333" rx="19.667" fill="#D9D9D9" stroke="black" stroke-width="0.667"/>
              </mask>
              <g mask="url(#mask-profile)">
                <rect width="40" height="40" rx="20" fill="var(--Accent-Green, #BCFF4E)"/>
                <ellipse cx="20" cy="16" rx="7" ry="7" fill="#1a1a1a" opacity="0.35"/>
                <ellipse cx="20" cy="38" rx="12" ry="12" fill="#1a1a1a" opacity="0.35"/>
                <rect x="1.25" y="1.25" width="37.5" height="37.5" rx="18.75" stroke="white" stroke-opacity="0.4" stroke-width="2.5"/>
              </g>
            </svg>
            <span v-if="unreadNotifCount > 0" class="layout-topbar__badge" @click.stop="showNotifications = !showNotifications">{{ unreadNotifCount > 9 ? '9+' : unreadNotifCount }}</span>
          </div>
          <span class="layout-topbar__name">{{ companyName }}</span>
          <svg class="layout-topbar__chevron" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 256 256"><path d="M181.66,133.66l-80,80a8,8,0,0,1-11.32-11.32L164.69,128,90.34,53.66a8,8,0,0,1,11.32-11.32l80,80A8,8,0,0,1,181.66,133.66Z"></path></svg>
        </div>
      </div>
      <router-view />
    </div>

    <!-- Notification Sidebar -->
    <Teleport to="body">
      <Transition name="notif-fade">
        <div v-if="showNotifications" class="notif-overlay" @click="showNotifications = false" />
      </Transition>
      <Transition name="notif-slide">
        <div v-if="showNotifications" class="notif-sidebar">
          <div class="notif-sidebar__header">
            <button class="notif-sidebar__back" @click="showNotifications = false">
              <svg width="20" height="20" viewBox="0 0 256 256" fill="currentColor"><path d="M165.66,202.34a8,8,0,0,1-11.32,11.32l-80-80a8,8,0,0,1,0-11.32l80-80a8,8,0,0,1,11.32,11.32L91.31,128Z"/></svg>
            </button>
            <span class="notif-sidebar__title">Notifications</span>
            <button v-if="unreadNotifCount > 0" class="notif-sidebar__mark-read" @click="markAllRead">Mark all as read</button>
          </div>

          <!-- Filter tabs -->
          <div class="notif-sidebar__tabs">
            <button
              v-for="tab in notifTabs"
              :key="tab.id"
              class="notif-sidebar__tab"
              :class="{ 'notif-sidebar__tab--active': activeNotifTab === tab.id }"
              @click="activeNotifTab = tab.id"
            >
              {{ tab.label }}
              <span v-if="tab.count > 0" class="notif-sidebar__tab-count">{{ tab.count }}</span>
            </button>
          </div>

          <!-- Notification list -->
          <div class="notif-sidebar__body">
            <div v-if="filteredNotifications.length === 0" class="notif-sidebar__empty">
              <svg width="48" height="48" viewBox="0 0 256 256" fill="currentColor"><path d="M221.8,175.94C216.25,166.38,208,139.33,208,104a80,80,0,1,0-160,0c0,35.34-8.26,62.38-13.81,71.94A16,16,0,0,0,48,200H88.81a40,40,0,0,0,78.38,0H208a16,16,0,0,0,13.8-24.06ZM128,216a24,24,0,0,1-22.62-16h45.24A24,24,0,0,1,128,216ZM48,184c7.7-13.24,16-43.92,16-80a64,64,0,1,1,128,0c0,36.05,8.28,66.73,16,80Z"/></svg>
              <span>No notifications yet</span>
            </div>
            <div v-else>
              <div
                v-for="notif in visibleNotifications"
                :key="notif.id"
                class="notif-item"
                :class="{ 'notif-item--unread': !notif.read }"
                @click="onNotifClick(notif)"
              >
                <div class="notif-item__icon">
                  <component :is="notifIconConfig[notif.iconType]?.component" :size="18" color="currentColor" weight="regular" />
                </div>
                <div class="notif-item__body">
                  <div class="notif-item__title">{{ notif.title }}</div>
                  <div class="notif-item__desc">{{ notif.description }}</div>
                  <div class="notif-item__time">{{ notif.timeAgo }}</div>
                </div>
                <div v-if="!notif.read" class="notif-item__dot"></div>
              </div>
              <div v-if="filteredNotifications.length > notifVisibleCount" class="notif-sidebar__see-more">
                <button @click="notifVisibleCount = filteredNotifications.length">
                  See {{ filteredNotifications.length - notifVisibleCount }} more
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import axios from '@/helpers/interceptors';
import { useScrollLock } from '@modules/shared/composables/useScrollLock';
import FloatingSidebar from '@modules/app/components/FloatingSidebar.vue';
import {
  PhArrowDownLeft,
  PhArrowUpRight,
  PhShieldCheck,
  PhCreditCard,
  PhWarning,
  PhPiggyBank,
  PhEnvelope,
  PhInfo,
  PhArrowsLeftRight,
} from '@phosphor-icons/vue';
import { getEntityId, getAccessToken } from '@/helpers/utils';
import { fetchTradeHistory, fetchOpenOrders } from '@modules/trading/services/tradingApi';
import { initSalesIQChat, useSalesIQChat } from '@modules/shared/composables/useSalesIQChat';

const vuexStore = useStore();
const router = useRouter();
const currentCompany = computed(() => vuexStore.getters['currentCompany']);
const currentUser = computed(() => vuexStore.getters['currentUser']);
const companyName = computed(() => currentCompany.value?.name || '');

// If preloader already restored company data from cache, no loading needed
const loading = ref(!currentCompany.value);

const userInitials = computed(() => {
  const user = currentUser.value;
  if (user) {
    const first = user.firstName ? user.firstName[0] : '';
    const last = user.surName ? user.surName[0] : '';
    if (first || last) return (first + last).toUpperCase();
  }
  const name = companyName.value;
  if (!name) return '';
  return name[0].toUpperCase();
});

// SalesIQ native chat
const { unreadCount: chatUnreadCount } = useSalesIQChat();

onMounted(async () => {
  // Preloader already fetches getCompanyById in Phase 1.
  // Only dispatch here as fallback if data is missing.
  if (!currentCompany.value || !currentUser.value) {
    const entityId = getEntityId();
    if (entityId) {
      // Don't await — let layout render immediately, data arrives reactively
      vuexStore.dispatch('getCompanyById', entityId).catch(() => {}).finally(() => {
        loading.value = false;
      });
    } else {
      loading.value = false;
    }
  } else {
    loading.value = false;
  }

  // Init SalesIQ native chat
  initSalesIQChat();

  // Prefetch trade history + open orders in background (cached for 5 min)
  fetchTradeHistory().catch(() => {});
  fetchOpenOrders().catch(() => {});

  // Fetch notifications for badge count
  fetchNotifications();
});

const handleHelpClick = () => {
  console.log('Help clicked');
};

const handleProfileClick = () => {
  console.log('Profile clicked');
};

// Notifications
const showNotifications = ref(false);
useScrollLock(showNotifications);
const activeNotifTab = ref('all');

const notifIconConfig = {
  'money-in': { component: PhArrowDownLeft },
  'money-out': { component: PhArrowUpRight },
  'security': { component: PhShieldCheck },
  'card': { component: PhCreditCard },
  'warning': { component: PhWarning },
  'savings': { component: PhPiggyBank },
  'chat': { component: PhEnvelope },
  'system': { component: PhInfo },
  'exchange': { component: PhArrowsLeftRight },
};

// ── Notifications (real API) ──
const notifications = ref([])
const notificationsLoading = ref(false)

function formatTimeAgo(dateStr) {
  const d = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHrs = Math.floor(diffMin / 60)
  if (diffHrs < 24) return `${diffHrs}h ago`
  const diffDays = Math.floor(diffHrs / 24)
  if (diffDays === 1) return 'Yesterday'
  return `${diffDays} days ago`
}

function categorizeNotification(templateName, text) {
  const t = (templateName || '').toLowerCase()
  const txt = (text || '').toLowerCase()
  if (t.includes('payment') || t.includes('transfer') || t.includes('crypto') || txt.includes('payment') || txt.includes('transfer')) {
    const isIncoming = txt.includes('received') || txt.includes('credited') || txt.includes('incoming')
    return { category: 'money', iconType: isIncoming ? 'money-in' : 'money-out', title: isIncoming ? 'Payment received' : 'Payment sent', route: '/app/payments' }
  }
  if (t.includes('exchange') || txt.includes('exchange')) return { category: 'money', iconType: 'exchange', title: 'Exchange completed', route: '/app/exchange' }
  if (t.includes('card') || txt.includes('card')) return { category: 'card', iconType: 'card', title: 'Card transaction', route: '/app/cards' }
  if (t.includes('login') || t.includes('security') || t.includes('otp') || txt.includes('login') || txt.includes('security')) return { category: 'security', iconType: 'security', title: 'Security alert', route: '/app/settings' }
  if (t.includes('chat') || t.includes('message') || t.includes('support') || txt.includes('support')) return { category: 'chat', iconType: 'chat', title: 'New message', route: '/app/chat' }
  if (t.includes('renting') || t.includes('saving') || txt.includes('renting') || txt.includes('interest')) return { category: 'savings', iconType: 'savings', title: 'Renting payout', route: '/app/renting' }
  return { category: 'system', iconType: 'system', title: 'Notification', route: '/app/payments' }
}

async function fetchNotifications() {
  notificationsLoading.value = true
  try {
    const token = getAccessToken() || ''
    const resp = await fetch('/api/notifications/list/0/50', {
      headers: { 'Authorization': `Bearer ${token}` },
    })
    if (!resp.ok) {
      notifications.value = []
      return
    }
    const data = await resp.json()
    const messages = data?.messages || data?.Messages || []
    notifications.value = messages.map((m, idx) => {
      const text = m.textEn || m.TextEn || m.textLc || m.TextLc || ''
      const cat = categorizeNotification(m.templateName || m.TemplateName || '', text)
      return {
        id: `notif-${idx}`,
        category: cat.category,
        title: cat.title,
        description: text,
        timeAgo: formatTimeAgo(m.date || m.Date || ''),
        read: false,
        iconType: cat.iconType,
        route: cat.route,
      }
    })
  } catch (e) {
    console.error('Failed to fetch notifications', e)
    notifications.value = []
  } finally {
    notificationsLoading.value = false
  }
}

const unreadNotifCount = computed(() => notifications.value.filter(n => !n.read).length);

const notifTabs = computed(() => [
  { id: 'all', label: 'All', count: notifications.value.length },
  { id: 'unread', label: 'Unread', count: unreadNotifCount.value },
  { id: 'money', label: 'Money', count: notifications.value.filter(n => n.category === 'money').length },
  { id: 'chat', label: 'Chat', count: notifications.value.filter(n => n.category === 'chat').length },
  { id: 'security', label: 'Security', count: notifications.value.filter(n => n.category === 'security').length },
  { id: 'card', label: 'Cards', count: notifications.value.filter(n => n.category === 'card').length },
]);

const notifVisibleCount = ref(7);

const filteredNotifications = computed(() => {
  if (activeNotifTab.value === 'all') return notifications.value;
  if (activeNotifTab.value === 'unread') return notifications.value.filter(n => !n.read);
  return notifications.value.filter(n => n.category === activeNotifTab.value);
});

const visibleNotifications = computed(() =>
  filteredNotifications.value.slice(0, notifVisibleCount.value)
);

function markAllRead() {
  notifications.value.forEach(n => { n.read = true; });
}

// Reset visible count when tab changes
watch(activeNotifTab, () => { notifVisibleCount.value = 7; });

// Fetch notifications when sidebar opens
watch(showNotifications, (open) => {
  if (open) fetchNotifications()
})

function onNotifClick(notif) {
  notif.read = true;
  showNotifications.value = false;
  if (notif.route) router.push(notif.route);
}
</script>

<style lang="scss" scoped>
.floating-slim-layout {
  display: flex;
  min-height: 100vh;
  gap: 3rem;
  padding: 3rem;
  background: var(--theme-bg-page);
}

.main-content {
  max-width: 944px;
  width: 100%;
  min-width: 0;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.layout-topbar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
}

.layout-topbar__chat-btn {
  position: relative;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--theme-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: var(--theme-topbar-hover);
    color: var(--theme-text-primary);
  }
}

.layout-topbar__chat-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: var(--theme-status-error);
  color: var(--theme-text-on-dark);
  font-size: 10px;
  font-weight: 700;
  font-family: 'Manrope', sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.layout-topbar__company {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 0;
  border-radius: 8px;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.7;
  }
}

.layout-topbar__avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--theme-text-primary);
}

.layout-topbar__avatar--initials {
  font-family: 'Manrope', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: var(--theme-text-primary);
}

.layout-topbar__avatar--profile {
  display: none;
}

.layout-topbar__badge {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--theme-green);
  color: var(--theme-text-on-dark);
  font-family: 'Manrope', sans-serif;
  font-size: 10px;
  font-weight: 600;
  display: none;
  align-items: center;
  justify-content: center;
  line-height: 1;
  cursor: pointer;
}

.layout-topbar__avatar-initials {
  font-family: 'Manrope', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: var(--theme-accent-text);
  line-height: 1;
}

.layout-topbar__name {
  font-family: 'Manrope', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: var(--theme-text-primary);
}

.layout-topbar__company--loading {
  cursor: default;
  &:hover {
    background: none;
  }
}

.layout-topbar__skeleton {
  background: var(--theme-skeleton-pulse-from);
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

.layout-topbar__skeleton--circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
}

.layout-topbar__skeleton--text {
  width: 120px;
  height: 16px;
  border-radius: 4px;
  display: inline-block;
}

@keyframes skeleton-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

// Consistent page header styles
:deep(.page-header) {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
}

// Consistent content grid
:deep(.content-grid) {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

// Mobile responsive
@media (max-width: 768px) {
  .floating-slim-layout {
    padding: 0.5rem;
    padding-bottom: calc(108px + env(safe-area-inset-bottom)); // room for fixed bottom nav bar
    gap: 0;
  }

  // Sidebar wrapper takes no space on mobile (sidebar is position:fixed at bottom)
  :deep(.sidebar-wrapper) {
    width: 0 !important;
    overflow: hidden;
  }

  .main-content {
    max-width: 100%;
  }

  .layout-topbar {
    justify-content: flex-start;
    flex-direction: row;
    padding: 4px 4px 0;
  }

  .layout-topbar__avatar {
    width: 40px;
    height: 40px;
  }

  .layout-topbar__avatar--initials {
    display: none;
  }

  .layout-topbar__chat-btn {
    display: none;
  }

  .layout-topbar__badge {
    display: flex;
  }

  .layout-topbar__avatar--profile {
    display: flex;
    border: none;
    background: none;
    position: relative;
  }

  .layout-topbar__name {
    display: none;
  }

  .layout-topbar__chevron {
    display: none;
  }

  :deep(.page-header) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  :deep(.content-grid) {
    gap: 1rem;
  }
}

@media (max-width: 480px) {
  .floating-slim-layout {
    padding: 0.5rem;
    padding-bottom: calc(108px + env(safe-area-inset-bottom));
  }
}
</style>

<!-- Unscoped: override legacy #app min-width on mobile -->
<style lang="scss">
@media (max-width: 768px) {
  #app:has(.floating-slim-layout) {
    min-width: 0;
  }
}

#zohohc-asap-web-button,
[class*="zohohc-asap-launcher"] {
  display: none !important;
}

// Notification sidebar (unscoped for Teleport)
.notif-fade-enter-active,
.notif-fade-leave-active {
  transition: opacity 0.3s ease;
}
.notif-fade-enter-from,
.notif-fade-leave-to {
  opacity: 0;
}

.notif-slide-enter-active,
.notif-slide-leave-active {
  transition: transform 0.3s ease;
}
.notif-slide-enter-from,
.notif-slide-leave-to {
  transform: translateX(100%);
}

.notif-overlay {
  position: fixed;
  inset: 0;
  background: var(--theme-bg-overlay);
  z-index: 9998;
}

.notif-sidebar {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 420px;
  max-width: 100vw;
  background: var(--theme-bg-card);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  box-shadow: -2px 0 12px rgba(0, 0, 0, 0.08);

  &__header {
    display: flex;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid var(--theme-border);
    flex-shrink: 0;
    gap: 16px;
  }

  &__back {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: flex;
    flex-shrink: 0;
    color: var(--theme-text-primary);

    &:hover { opacity: 0.7; }
  }

  &__title {
    font-family: 'Manrope', sans-serif;
    font-size: 20px;
    font-weight: 700;
    color: var(--theme-text-primary);
  }

  &__tabs {
    display: flex;
    gap: 4px;
    padding: 12px 24px;
    border-bottom: 1px solid var(--theme-border);
    overflow-x: auto;
    flex-shrink: 0;
  }

  &__tab {
    padding: 6px 12px;
    border: none;
    border-radius: 999px;
    font-family: 'Calling Code', monospace;
    font-size: 13px;
    font-weight: 400;
    cursor: pointer;
    background: transparent;
    color: var(--theme-text-secondary);
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 4px;
    transition: all 0.15s;

    &--active {
      background: var(--theme-text-primary);
      color: var(--theme-bg-card);
    }

    &:hover:not(&--active) {
      background: var(--theme-bg-subtle);
    }
  }

  &__tab-count {
    font-size: 11px;
    opacity: 0.7;
  }

  &__mark-read {
    background: none;
    border: none;
    font-family: 'Calling Code', monospace;
    font-size: 13px;
    color: var(--theme-green);
    cursor: pointer;
    padding: 0;
    margin-left: auto;
    white-space: nowrap;

    &:hover { opacity: 0.7; }
  }

  &__see-more {
    padding: 12px 24px;

    button {
      box-sizing: border-box;
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      padding: 8px 24px;
      width: 100%;
      height: 40px;
      background: transparent;
      border: 1px solid var(--theme-green);
      border-radius: 4px;
      font-family: 'Calling Code', monospace;
      font-size: 16px;
      color: var(--theme-green);
      cursor: pointer;
    }
  }

  &__body {
    flex: 1;
    overflow-y: auto;
    padding: 8px 0;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 60px 24px;
    color: var(--theme-text-secondary);
    font-family: 'Manrope', sans-serif;
    font-size: 15px;
  }
}

.notif-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 24px;
  cursor: pointer;
  transition: background 0.15s;
  position: relative;

  &:hover {
    background: var(--theme-bg-subtle);
  }

  &--unread {
    background: rgba(28, 165, 92, 0.04);
  }

  &__icon {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--theme-bg-icon);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: var(--theme-text-secondary);
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-family: 'Manrope', sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: var(--theme-text-primary);
    margin-bottom: 2px;
  }

  &__desc {
    font-family: 'Manrope', sans-serif;
    font-size: 13px;
    color: var(--theme-text-secondary);
    line-height: 18px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__time {
    font-family: 'Calling Code', monospace;
    font-size: 12px;
    color: var(--theme-text-muted);
    margin-top: 4px;
  }

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--theme-green);
    flex-shrink: 0;
    margin-top: 6px;
  }
}

// ── Mobile: notification sidebar as bottom sheet ──
@media (max-width: 1024px) {
  .notif-overlay {
    touch-action: none;
  }

  .notif-sidebar {
    top: auto;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    max-width: 100%;
    height: 90vh;
    border-radius: 24px 24px 0 0;
    box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.12);

    &__header {
      padding: 20px 16px;
    }

    &__tabs {
      padding: 12px 16px;
    }

    &__empty {
      padding: 60px 16px;
    }

    &__see-more {
      padding: 12px 16px;
    }
  }

  .notif-item {
    padding: 14px 16px;
  }

  .notif-slide-enter-active,
  .notif-slide-leave-active {
    transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .notif-slide-enter-from,
  .notif-slide-leave-to {
    transform: translateY(100%);
  }
}
</style>
